/**
 * M1 Signal Intelligence — governed Signal model.
 *
 * A signal is NOT a Problem Record. It is governed evidence of a potential
 * issue that must receive a human, auditable disposition.
 */

export type SignalClass =
  | "warranty_field"
  | "production"
  | "internal_quality"
  | "supplier"
  | "engineering"
  | "financial"
  | "compliance_safety";

export const SIGNAL_CLASS_LABELS: Record<SignalClass, string> = {
  warranty_field: "Warranty / Field",
  production: "Production",
  internal_quality: "Internal Quality",
  supplier: "Supplier",
  engineering: "Engineering",
  financial: "Financial",
  compliance_safety: "Compliance / Safety",
};

export type SignalStatus = "open" | "monitoring" | "linked" | "converted" | "closed-nfa";

export const SIGNAL_STATUS_LABELS: Record<SignalStatus, string> = {
  open: "Awaiting disposition",
  monitoring: "Monitoring",
  linked: "Linked to record",
  converted: "Problem Record created",
  "closed-nfa": "No further action",
};

export type Disposition = "monitor" | "link" | "create" | "nfa";

export const DISPOSITION_LABELS: Record<Disposition, string> = {
  monitor: "Monitor",
  link: "Link to Existing Problem Record",
  create: "Create New Problem Record",
  nfa: "No Further Action",
};

/** MVP default scale; configurable through policy_settings `signal.severity_scale`. */
export const DEFAULT_SEVERITY_SCALE: Record<number, string> = {
  5: "Critical",
  4: "High",
  3: "Moderate",
  2: "Low",
  1: "Minor",
};

export interface SignalPoint {
  month: string;
  value: number;
}

export interface Signal {
  id: string;
  code: string;
  signalClass: SignalClass;
  title: string;
  summary: string;
  programId: string | null;
  component: string;
  supplierName: string;
  plant: string;
  detectedAt: string;
  sourceSystemId: string | null;
  sourceReference: string;
  sourceTimestamp: string | null;
  magnitude: number;
  magnitudeUnit: string;
  trend: SignalPoint[];
  trendDirection: "rising" | "falling" | "stable";
  exposureUnits: number;
  financialExposure: number;
  recurrenceCount: number;
  safetyRelevant: boolean;
  complianceRelevant: boolean;
  customerImpact: number;
  dataConfidence: number;
  status: SignalStatus;
  currentDisposition: Disposition | null;
  currentSeverity: number | null;
  severityRationale: string;
  aiSuggestedSeverity: number | null;
  linkedRecordId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SignalLink {
  id: string;
  signalId: string;
  relatedSignalId: string | null;
  claimId: string | null;
  linkType: "duplicate" | "related" | "claim-source";
  rationale: string;
  createdBy: string;
}

export interface RootCauseEvidence {
  id: string;
  signalId: string;
  statement: string;
  source: string;
  verifiedBy: string;
  verificationMethod: string;
  evidenceRef: string;
  verifiedAt: string | null;
  carriedToRecordId: string | null;
}

export interface DispositionEntry {
  id: string;
  signalId: string;
  disposition: Disposition;
  rationale: string;
  targetRecordId: string | null;
  decidedByLabel: string;
  authorityRuleKey: string | null;
  auditEventId: string | null;
  evidenceSnapshot: Record<string, unknown>;
  createdAt: string;
}

export interface SeverityEntry {
  id: string;
  signalId: string;
  severity: number;
  rationale: string;
  aiSuggested: number | null;
  assessedByLabel: string;
  authorityRuleKey: string | null;
  secondApprovalRequired: boolean;
  createdAt: string;
}
